import {
  Link,
  useLoaderData,
  useRevalidator,
  useRouteError,
} from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import TransmedProcessService from "../../service/transmed/process";
import TransmedProcessFormSectionService from "../../service/transmed/process/form/section";
import TransmedRegistrationService from "../../service/transmed/process/registration/registration";
import TransmedAdmin from "../../dtos/transmed/process/admin";
import OutputTransmedFormSection from "../../dtos/transmed/process/form/section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircle,
  faClose,
  faEdit,
  faFile,
  faSadTear,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { externalRedirectUrl } from "../page-redirect";
import NumberInput from "../../components/inputs/number";
import CheckboxInput from "../../components/inputs/checkbox";
import { IMAGE_HOST } from "../../server/config";
import OutputTransmedFormField from "../../dtos/transmed/process/form/field";
import TextInput from "../../components/inputs/text";
import OutputTransmedEdition from "../../dtos/transmed/process/edition";
import TransmedRegistrationFieldService from "../../service/transmed/process/registration/field";
import { CircularProgress } from "@mui/material";
import InputDialog from "../../components/dialog";
import SearchBox from "../../components/inputs/search-box";
import subtractOneSecond from "../../utils/subtractOneSecond";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {
  PublicTransmedRegistration,
  TransmedRegistrationStage,
} from "../../dtos/transmed/process/registration/registration";
import TransmedRegistrationField from "../../dtos/transmed/process/registration/field";

export const path = "transmed/registrations";

type LoaderType = {
  admin: TransmedAdmin;
  formSections: OutputTransmedFormSection[];
  registrations: PublicTransmedRegistration[];
  edition: OutputTransmedEdition;
};

export async function loader(): Promise<LoaderType> {
  const admin = new TransmedProcessService().read.admin();
  const formSections = new TransmedProcessFormSectionService().read.current();
  const registrations = new TransmedRegistrationService().read.outgoings();
  const edition = new TransmedProcessService().read.edition();

  return {
    admin: await admin,
    formSections: await formSections,
    registrations: await registrations,
    edition: await edition,
  };
}

function RegistrationField({
  field,
  registrationField,
  registrationId,
  disabled = false,
}: {
  field: OutputTransmedFormField;
  registrationField: TransmedRegistrationField | null;
  registrationId: number;
  disabled: boolean;
}) {
  const { admin } = useLoaderData() as LoaderType;

  const [quantity, setQuantity] = useState<number>(
    registrationField?.quantity ?? 0
  );
  const [answer, setAnswer] = useState<string>(registrationField?.answer ?? "");

  useEffect(() => {
    setQuantity(registrationField?.quantity ?? 0);
    setAnswer(registrationField?.answer ?? "");
  }, [registrationField]);

  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [loading, setLoading] = useState<boolean>(false);

  const { width } = useWindowDimensions();

  return (
    <div
      className="surface"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--gap-small)",
      }}
    >
      <div
        style={{
          marginLeft: width > 400 ? "calc(90px - var(--surface-padding))" : 0,
        }}
      >
        {field.title}
        <div className="label">{field.subtitle}</div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "var(--gap-small)",
        }}
      >
        <div
          style={{
            width: "calc(90px - 2 * var(--surface-padding))",
          }}
          className="label"
        >
          {field.points}p
          {field.quantitative && (
            <>
              <br />/{field.unit_singular}
            </>
          )}
        </div>

        {field.quantitative ? (
          <NumberInput
            required
            label={"Nr. " + field.unit_plural}
            disabled={disabled || !admin.validate}
            value={quantity}
            onChange={(e) =>
              setQuantity(
                isNaN(parseFloat(e.target.value))
                  ? 0
                  : parseFloat(e.target.value)
              )
            }
          />
        ) : (
          <CheckboxInput
            disabled={disabled || !admin.validate}
            checked={quantity !== 0}
            onChange={(e, checked) => setQuantity(checked ? 1 : 0)}
          />
        )}
        {registrationField !== null ? (
          registrationField.proof !== null ? (
            <Link
              to={externalRedirectUrl(IMAGE_HOST + registrationField.proof)}
              className="button primary text"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faFile} /> Dovada
            </Link>
          ) : (
            <i style={{ color: "var(--color-fasmr-blue)" }}>(fără dovadă)</i>
          )
        ) : (
          <i>(neselectat)</i>
        )}
        <div className="stretcher" />
        {(loading || revalidator.state === "loading") && <CircularProgress />}
        <span
          className="label"
          style={{
            padding: "var(--gap-xs) var(--gap-small)",
            borderRadius: 1000,
            backgroundColor:
              registrationField === null
                ? "var(--color-dark)"
                : registrationField.validator !== null
                ? registrationField.valid
                  ? "green"
                  : "red"
                : "yellow",
            color:
              registrationField !== null && registrationField.validator === null
                ? "var(--color-dark)"
                : "white",
          }}
        >
          {registrationField === null
            ? "Neselectat"
            : registrationField.validator !== null
            ? registrationField.valid
              ? "Valid"
              : "Invalid"
            : "Nevalidat"}
        </span>
        <TextInput
          disabled={disabled || !admin.validate}
          label="Răspuns"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
        />
        {admin.validate && (
          <span>
            <button
              className="primary button"
              style={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                ...{ "--color": "green" },
              }}
              disabled={
                disabled ||
                !admin.validate ||
                (registrationField === null && quantity === 0) ||
                (registrationField !== null &&
                  registrationField.validator !== null &&
                  registrationField.quantity === quantity &&
                  (registrationField.answer ?? "") === answer &&
                  registrationField.valid)
              }
              onClick={async () => {
                setError("");
                setLoading(true);

                const service = new TransmedRegistrationFieldService();
                try {
                  await service.update.validate(
                    registrationField?.id ??
                      (
                        await service.create.registrationField(
                          field.id,
                          registrationId
                        )
                      ).id,
                    quantity,
                    answer.trim().length > 0 ? answer.trim() : null
                  );
                } catch (e) {
                  setError(e);
                } finally {
                  revalidator.revalidate();
                  setLoading(false);
                }
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
            <button
              className="secondary button"
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                ...{ "--color": "red" },
              }}
              disabled={
                disabled ||
                !admin.validate ||
                (registrationField === null && quantity === 0) ||
                (registrationField !== null &&
                  registrationField.validator !== null &&
                  registrationField.quantity === quantity &&
                  (registrationField.answer ?? "") === answer &&
                  !registrationField.valid)
              }
              onClick={async () => {
                setError("");
                setLoading(true);

                const service = new TransmedRegistrationFieldService();
                try {
                  await service.update.invalidate(
                    registrationField?.id ??
                      (
                        await service.create.registrationField(
                          field.id,
                          registrationId
                        )
                      ).id,
                    quantity,
                    answer.trim().length > 0 ? answer.trim() : null
                  );
                } catch (e) {
                  setError(e);
                } finally {
                  revalidator.revalidate();
                  setLoading(false);
                }
              }}
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </span>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "var(--gap-xs)",
            alignSelf: "stretch",
          }}
        >
          <div className="label">Ultimul validator:</div>
          <div>{registrationField?.validator ?? <i>(nevalidat)</i>}</div>
        </div>
      </div>
    </div>
  );
}

function Registration({
  registration,
  open,
  onSetOpen,
}: {
  registration: PublicTransmedRegistration;
  open: boolean;
  onSetOpen: () => void;
}) {
  const { admin, formSections, edition } = useLoaderData() as LoaderType;

  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogCallback, setDialogCallback] = useState<
    (() => Promise<void> | void) | null
  >(null);
  const [dialogType, setDialogType] = useState<TransmedRegistrationStage>(
    TransmedRegistrationStage.valid
  );
  const [answer, setAnswer] = useState<string>("");
  const [sendReject, setSendReject] = useState<boolean>(false);

  useEffect(() => {
    if (sendReject) {
      setError("");
      setLoading(true);
      new TransmedRegistrationService().update
        .reject(registration.id, answer.trim())
        .catch((error) => setError(error))
        .finally(() => {
          revalidator.revalidate();
          setLoading(false);
        });
    }
    setSendReject(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendReject]);

  const [sectionId, setSectionId] = useState<number | null>(null);
  const [subsectionId, setSubsectionId] = useState<number | null>(null);

  function getPoints(section: OutputTransmedFormSection): string {
    return registration.fields
      .reduce((acc, rfield) => {
        for (const ss of section.subsections)
          for (const f of ss.fields)
            if (f.id === rfield.field) return acc + f.points * rfield.quantity;
        return acc;
      }, 0)
      .toFixed(2);
  }

  return (
    <div
      className="surface"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--gap-small)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--gap-small)",
        }}
      >
        <span
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "var(--gap-small)",
            flex: 1,
          }}
        >
          <h3>
            {registration.user.first_name} {registration.user.last_name}
          </h3>
          <i style={{ wordBreak: "break-word" }}>
            ({registration.user.public_id})
          </i>
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "var(--gap-small)",
          }}
        >
          {loading || (revalidator.state === "loading" && <CircularProgress />)}
          {registration.points && (
            <h3
              style={{
                color: "var(--color-fasmr-blue)",
                fontWeight: "var(--display-font-weight)",
              }}
              title={registration.points.toString()}
            >
              {registration.points.toFixed(2)}
            </h3>
          )}
          <span
            style={{
              backgroundColor:
                registration.stage === TransmedRegistrationStage.incomplete
                  ? "var(--color-dark)"
                  : registration.stage === TransmedRegistrationStage.submitted
                  ? "yellow"
                  : registration.stage === TransmedRegistrationStage.valid
                  ? "green"
                  : registration.stage === TransmedRegistrationStage.returned
                  ? "var(--color-dark)"
                  : registration.stage === TransmedRegistrationStage.rejected
                  ? "red"
                  : registration.stage === TransmedRegistrationStage.confirmed
                  ? "yellow"
                  : "var(--color-fasmr-blue)",
              color:
                registration.stage === TransmedRegistrationStage.submitted ||
                registration.stage === TransmedRegistrationStage.confirmed
                  ? "var(--color-dark)"
                  : "white",
              padding: "var(--gap-xs) var(--gap-small)",
              borderRadius: 1000,
            }}
            className="label"
          >
            {registration.stage === TransmedRegistrationStage.incomplete
              ? "Netrimis"
              : registration.stage === TransmedRegistrationStage.submitted
              ? "Nevalidat"
              : registration.stage === TransmedRegistrationStage.valid
              ? "Validat"
              : registration.stage === TransmedRegistrationStage.returned
              ? "Returnat"
              : registration.stage === TransmedRegistrationStage.rejected
              ? "Revocat"
              : registration.stage === TransmedRegistrationStage.chosing
              ? "Alege centrul"
              : registration.stage === TransmedRegistrationStage.distributed
              ? "Repartizat"
              : registration.stage === TransmedRegistrationStage.revoked
              ? "Renunțat"
              : registration.stage === TransmedRegistrationStage.no_place
              ? "Fără loc"
              : registration.stage === TransmedRegistrationStage.confirmed
              ? "Confirmat pentru: " + registration.confirmed_lc?.name_short
              : registration.stage === TransmedRegistrationStage.accepted
              ? "Acceptat la: " + registration.confirmed_lc?.name_short
              : "Necunoscut"}
          </span>

          <button className="primary button text" onClick={onSetOpen}>
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
          </button>
        </div>
        {admin.validate && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "var(--gap-small)",
            }}
          >
            <button
              disabled={
                !admin.validate ||
                registration.stage !== TransmedRegistrationStage.submitted
              }
              className="primary button"
              style={{ margin: 0, ...{ "--color": "green" } }}
              onClick={() => {
                setDialogType(TransmedRegistrationStage.valid);
                setDialogCallback(() => async () => {
                  setError("");
                  setLoading(true);

                  try {
                    await new TransmedRegistrationService().update.validate(
                      registration.id
                    );
                  } catch (error) {
                    setError(error);
                  } finally {
                    revalidator.revalidate();
                    setLoading(false);
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
            <button
              disabled={
                !admin.validate ||
                registration.stage !== TransmedRegistrationStage.submitted ||
                edition.registration_end < new Date()
              }
              className="secondary button"
              onClick={() => {
                setDialogType(TransmedRegistrationStage.returned);
                setDialogCallback(() => async () => {
                  setError("");
                  setLoading(true);

                  try {
                    await new TransmedRegistrationService().update.return(
                      registration.id
                    );
                    revalidator.revalidate();
                  } catch (error) {
                    setError(error);
                  } finally {
                    setLoading(false);
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={faUndo} />
            </button>
            <button
              disabled={
                !admin.validate ||
                registration.stage !== TransmedRegistrationStage.submitted
              }
              className="primary button"
              style={{ margin: 0, ...{ "--color": "red" } }}
              onClick={() => {
                setDialogType(TransmedRegistrationStage.rejected);
                setDialogCallback(() => () => {
                  setSendReject(true);
                });
              }}
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
            <button
              disabled={
                !admin.validate ||
                edition.points_deadline_second < new Date() ||
                (registration.stage !== TransmedRegistrationStage.valid &&
                  registration.stage !== TransmedRegistrationStage.returned &&
                  registration.stage !== TransmedRegistrationStage.rejected)
              }
              className="primary button"
              onClick={async () => {
                setError("");
                setLoading(true);

                try {
                  await new TransmedRegistrationService().update.edit(
                    registration.id
                  );
                  revalidator.revalidate();
                } catch (error) {
                  setError(error);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
          </div>
        )}
      </div>
      {open && (
        <>
          <div
            style={{
              display: "flex",
              gap: "var(--gap-small)",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              Nume: <b>{registration.user.last_name}</b>
            </div>
            <div>
              Prenume: <b>{registration.user.first_name}</b>
            </div>
            <div>
              Specializare:{" "}
              <b>
                {registration.user.is_medicine
                  ? "Medicină"
                  : registration.user.programme_title}
              </b>
            </div>
            <div>
              An: <b>{registration.user.year}</b>
            </div>
            <div>
              Serie: <b>{registration.user.series}</b>
            </div>
            <div>
              Grupă: <b>{registration.user.group}</b>
            </div>
            <div>
              Număr de înmatriculare:{" "}
              <b>{registration.user.registration_number}</b>
            </div>
            <div>
              Email: <b>{registration.user.email}</b>
            </div>
          </div>
          {formSections.map((section) => {
            return (
              <div
                key={section.id}
                className="surface"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--gap-small)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "var(--gap-small)",
                  }}
                >
                  <div
                    style={{
                      width: "90px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {section.subsections.find(
                      (ss) =>
                        ss.fields.find(
                          (f) =>
                            registration.fields.find(
                              (rf) => rf.field === f.id && rf.quantity !== 0
                            ) !== undefined
                        ) !== undefined
                    ) !== undefined && (
                      <FontAwesomeIcon
                        icon={faCircle}
                        style={{
                          marginLeft: "var(--surface-padding)",
                          border:
                            section.subsections.find(
                              (ss) =>
                                ss.fields.find(
                                  (f) =>
                                    registration.fields.find(
                                      (rf) =>
                                        rf.field === f.id &&
                                        rf.validator !== null &&
                                        !rf.valid
                                    ) !== undefined
                                ) !== undefined
                            ) !== undefined
                              ? "2px solid red"
                              : "none",
                          borderRadius: 1000,
                        }}
                        color={
                          section.subsections.find(
                            (ss) =>
                              ss.fields.find(
                                (f) =>
                                  registration.fields.find(
                                    (rf) =>
                                      rf.field === f.id && rf.validator === null
                                  ) !== undefined
                              ) !== undefined
                          ) !== undefined
                            ? "var(--color-fasmr-blue)"
                            : "inherit"
                        }
                      />
                    )}
                  </div>
                  <h4>{section.title}</h4>
                  <div className="stretcher" />
                  <div
                    style={{
                      marginRight: "var(--gap-xs)",
                    }}
                  >
                    {(
                      (section.weight /
                        formSections.reduce(
                          (acc, curr) => acc + curr.weight,
                          0
                        )) *
                      edition.max_points
                    ).toFixed(0)}
                    %{" "}
                    <b
                      style={{
                        color: "var(--color-fasmr-blue)",
                        fontWeight: "var(--title-font-weight)",
                      }}
                    >
                      {getPoints(section)}
                    </b>
                  </div>
                  <button
                    type="button"
                    className="primary text button"
                    onClick={() =>
                      setSectionId((sid) =>
                        sid === section.id ? null : section.id
                      )
                    }
                  >
                    <FontAwesomeIcon
                      icon={
                        section.id === sectionId ? faChevronUp : faChevronDown
                      }
                    />
                  </button>
                </div>
                {section.id === sectionId && (
                  <>
                    {section.subsections.map((subsection) => (
                      <div
                        key={subsection.id}
                        className="surface"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "var(--gap-small)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            textAlign: "left",
                          }}
                        >
                          <div
                            style={{
                              width: "calc(90px - var(--surface-padding))",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "var(--surface-padding)",
                            }}
                          >
                            {subsection.fields.find(
                              (f) =>
                                registration.fields.find(
                                  (rf) => rf.field === f.id && rf.quantity !== 0
                                ) !== undefined
                            ) !== undefined && (
                              <FontAwesomeIcon
                                icon={faCircle}
                                color={
                                  subsection.fields.find(
                                    (f) =>
                                      registration.fields.find(
                                        (rf) =>
                                          rf.field === f.id &&
                                          rf.validator === null
                                      ) !== undefined
                                  ) !== undefined
                                    ? "var(--color-fasmr-blue)"
                                    : "inherit"
                                }
                                style={{
                                  border:
                                    subsection.fields.find(
                                      (f) =>
                                        registration.fields.find(
                                          (rf) =>
                                            rf.field === f.id &&
                                            rf.validator !== null &&
                                            !rf.valid
                                        ) !== undefined
                                    ) !== undefined
                                      ? "2px solid red"
                                      : "none",
                                  borderRadius: 1000,
                                }}
                              />
                            )}
                          </div>
                          <b style={{ flex: 1 }}>{subsection.title}</b>
                          <button
                            type="button"
                            className="primary text button"
                            onClick={() =>
                              setSubsectionId((ssid) =>
                                ssid === subsection.id ? null : subsection.id
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={
                                subsection.id === subsectionId
                                  ? faChevronUp
                                  : faChevronDown
                              }
                            />
                          </button>
                        </div>
                        {subsection.id === subsectionId &&
                          subsection.fields.map((field) => (
                            <RegistrationField
                              key={field.id}
                              field={field}
                              registrationField={
                                registration.fields.find(
                                  (rf) => rf.field === field.id
                                ) ?? null
                              }
                              disabled={
                                registration.stage !==
                                TransmedRegistrationStage.submitted
                              }
                              registrationId={registration.id}
                            />
                          ))}
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
      <InputDialog
        title={
          dialogType === TransmedRegistrationStage.rejected
            ? "Revocă înscrierea"
            : dialogType === TransmedRegistrationStage.returned
            ? "Returnează înscrierea"
            : "Validează înscrierea"
        }
        continue_label={
          dialogType === TransmedRegistrationStage.rejected
            ? "Revocă"
            : dialogType === TransmedRegistrationStage.returned
            ? "Returnează"
            : "Validează"
        }
        abort_label="Anulează"
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      >
        {dialogType === TransmedRegistrationStage.returned ? (
          <>
            Urmează să returnezi această înscriere. Cât timp înscrierea se află
            în acest stadiu, participantul va putea edita documentele încărcate
            și retrimite înscrierea. Toate înscrierile returnate se retrimit
            automat la termenul finalizării înscrierilor.{" "}
          </>
        ) : (
          <>
            Această acțiune este reversibilă până în{" "}
            <b>
              {subtractOneSecond(
                edition.points_deadline_second
              ).toLocaleDateString("RO-ro")}
            </b>
            , ora{" "}
            <b>
              {subtractOneSecond(
                edition.points_deadline_second
              ).toLocaleTimeString("RO-ro")}
            </b>
            . După aceasta nu se mai pot aduce modificări{" "}
          </>
        )}
        {dialogType === TransmedRegistrationStage.rejected && (
          <div style={{ margin: "var(--gap-medium) 0" }}>
            <b style={{ color: "red" }}>
              ! Urmează să revoci această înscriere. Acest student nu va mai
              putea participa la această ediție Transmed!
            </b>
            <TextInput
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              label="Motivul revocării"
              required
              sx={{ marginTop: "var(--gap-medium)", width: "100%" }}
              multiline
            />
          </div>
        )}
        Ești sigur că vrei să faci aceasta?
      </InputDialog>
    </div>
  );
}

export function Component() {
  const { registrations } = useLoaderData() as LoaderType;
  const [search, setSearch] = useState<string>("");
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    registrations.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
  }, [registrations]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--gap-small)",
      }}
    >
      <h1>Înscrieri transmed</h1>
      <SearchBox
        placeholder="Caută după nume sau id public..."
        onSearch={setSearch}
        style={{ marginBottom: "var(--gap-medium)" }}
        searchOnClear={true}
      />
      {(search.trim().length === 0
        ? registrations
        : registrations.filter((registration) =>
            (
              registration.user.first_name +
              " " +
              registration.user.last_name +
              " " +
              registration.user.public_id
            ).includes(search.trim())
          )
      ).length === 0 ? (
        <span>
          <FontAwesomeIcon icon={faSadTear} color="var(--color-fasmr-blue)" />{" "}
          Nicio înscriere din partea centrului tău
        </span>
      ) : (
        (search.trim().length === 0
          ? registrations
          : registrations.filter((registration) =>
              (
                registration.user.first_name +
                " " +
                registration.user.last_name +
                " " +
                registration.user.public_id
              ).includes(search.trim())
            )
        ).map((registration) => (
          <Registration
            key={registration.id}
            registration={registration}
            open={openId === registration.id}
            onSetOpen={() =>
              setOpenId((oid) =>
                oid === registration.id ? null : registration.id
              )
            }
          />
        ))
      )}
    </div>
  );
}
Component.displayName = "page-account-transmed-registrations";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-registrations-error";
